import { englishError } from "./databaseErrors";
import { useCallback, useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { englishComponentName, englishMaterialAlias, englishMaterialName, sortMaterials } from "./materialNames";

export function variantMaterials(variantId, links, materials) {
  return links.filter((link) => String(link.variant_id) === String(variantId)).map((link) => {
    const material = materials.find((row) => String(row.id) === String(link.material_id));
    return `${link.part_name ? `${englishComponentName(link.part_name)}: ` : ""}${englishMaterialName(material)}`;
  }).join(" + ");
}

export function configurationLabel(variant, products, links, materials) {
  const product = products.find((row) => String(row.id) === String(variant.product_id));
  return `${product?.name || "-"} · ${englishMaterialAlias(variant.name, materials)} · ${variant.size_label} · ${variantMaterials(variant.id, links, materials)}`;
}

export function validateEndProductVariant(form) {
  if (!form.sku.trim() || !form.name.trim() || !form.size_label.trim()) return "Enter a configuration code, name, and size.";
  if (form.price !== "" && form.price != null && (!Number.isFinite(Number(form.price)) || Number(form.price) < 0)) return "Enter a valid price per piece, zero or more.";
  for (const field of ["length_cm", "width_cm", "height_cm"]) {
    if (form[field] !== "" && (!Number.isFinite(Number(form[field])) || Number(form[field]) <= 0)) return "Optional dimensions must be positive.";
  }
  if (!form.materials.length || form.materials.some((row) => !row.material_id)) return "Select at least one material for the configuration.";
  const keys = form.materials.map((row) => `${row.material_id}:${row.part_name.trim()}`);
  if (new Set(keys).size !== keys.length) return "Do not repeat a material for the same part.";
  return "";
}

export function useEndProducts(showroomId = null, companyCode = "refaq") {
  const [data, setData] = useState({ products: [], variants: [], links: [], materials: [], stocks: [], showrooms: [], reservations: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const reload = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const companyResult = await supabase.from("business_company").select("id, code, name, parent_company_id").eq("code", companyCode);
      if (companyResult.error) throw companyResult.error;
      const company = companyResult.data?.[0];
      if (!company) throw new Error("Company has not been configured in the database.");
      let stockQuery = supabase.from("end_product_stock").select("*");
      if (showroomId != null) stockQuery = stockQuery.eq("showroom_id", showroomId);
      const results = await Promise.all([
        supabase.from("end_product").select("*").eq("company_id", company.id).order("id", { ascending: false }),
        supabase.from("end_product_variant").select("*").order("id"),
        supabase.from("end_product_variant_material").select("*"),
        supabase.from("material").select("*"), stockQuery,
        supabase.from("showroom").select("id, name"),
      ]);
      const failed = results.find((result) => result.error);
      if (failed) throw failed.error;
      const reservations = [];
      for (let start = 0; ; start += 1000) {
        const result = await supabase.from('finished_reservation').select('*').eq('company_id', company.id).order('id').range(start, start + 999);
        if (result.error) throw result.error;
        reservations.push(...(result.data || []));
        if (!result.data || result.data.length < 1000) break;
      }
      const keys = ["products", "variants", "links", "materials", "stocks", "showrooms"];
      const next = Object.fromEntries(results.map((result, index) => [keys[index], result.data || []]));
      const productIds = new Set(next.products.map((row) => String(row.id)));
      next.variants = next.variants.filter((row) => productIds.has(String(row.product_id)));
      const variantIds = new Set(next.variants.map((row) => String(row.id)));
      next.links = next.links.filter((row) => variantIds.has(String(row.variant_id)));
      next.stocks = next.stocks.filter((row) => variantIds.has(String(row.variant_id)));
      next.company = company;
      next.materials = sortMaterials(next.materials, englishMaterialName);
      next.reservations = reservations;
      setData(next); return next;
    } catch (err) { setError("Unable to load finished products: " + englishError(err.message)); return null; }
    finally { setLoading(false); }
  }, [showroomId, companyCode]);
  useEffect(() => { reload(); }, [reload]);
  return { ...data, loading, error, reload };
}
